import {AxiosError} from 'axios';
import BASE_URL from '../../config/env';
import {apiClient} from '../apiClient';
import {
  IAddFolderRequest,
  IAddFolderResponse,
  IAddTagGroupRequest,
  IAddTagGroupResponse,
  IAddTagRequest,
  IAddTagResponse,
  IGetEditTagOrTagGroupResponse,
  ITagsFoldersViewData,
  ITagsList,
  ITagsListResponse,
  IUpdateTagOrTagGroupRequest,
  IUpdateTagOrTagGroupResponse,
  IGetBulkTagTallyReportStatusRes,
  IGetTaggingConflictDataReqPayload,
  IGetTaggingConflictDataRes,
  ISaveBulkTagReportDataRes,
  ISuperMasterDocListGenReqPayload,
  ISuperMasterDocListGenRes,
  ISaveBulkTagReportDataReqPayload,
  IGetBulkTagTallyReportStatusReqPayload,
  IDeleteTagGroupOrTagRequest,
  IAddFolderGroupRequest,
  IAddFolderGroupResponse,
  IGetFolderOrFolderGroupRequestParams,
  IGetFolderOrFolderGroupResponse,
  IUpdateFolderOrFolderGroupsRequest,
  IUpdateFolderOrFolderGroupsResponse,
  IMoveFolderOrFolderGroup,
  IMoveFolderOrFolderGroupRes,
  IMoveTagOrTagGroupPayload,
  IMoveTagOrTagGroupResponse,
} from '../types/tagFoldering.type';
import {
  BackendErrorData,
  parseApiErrorWithLocalization,
} from '../../utils/custom-errors/parseApiError';

const tagsAndFoldersEndpoint = (path: string) =>
  `${BASE_URL}/tagsfoldering/api/v1${path}`;

export const addTag = async (
  payload: IAddTagRequest
): Promise<IAddTagResponse> => {
  const {data} = await apiClient.post(
    tagsAndFoldersEndpoint('/tagsandfolders/tags'),
    payload
  );
  return data;
};

export const addFolder = async (
  payload: IAddFolderRequest
): Promise<IAddFolderResponse> => {
  const {data} = await apiClient.post(
    tagsAndFoldersEndpoint('/tagsandfolders/folders'),
    payload
  );
  return data;
};

export const getTagsList = async (
  params: string
): Promise<ITagsListResponse> => {
  const {data} = await apiClient.get(
    tagsAndFoldersEndpoint(`/tagsandfolders/list/tag${params}`)
  );
  return data;
};

export const getTagsFoldersViewData =
  async (): Promise<ITagsFoldersViewData> => {
    const {data} = await apiClient.get(
      tagsAndFoldersEndpoint(`/tagsandfolders/view`)
    );
    return data;
  };

export const addTagGroup = async (
  payload: IAddTagGroupRequest
): Promise<IAddTagGroupResponse> => {
  try {
    const {data} = await apiClient.post(
      tagsAndFoldersEndpoint('/tagsandfolders/tag-groups'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'TagsAndFolders'
    );
  }
};

export const getEditTagOrTagGroup = async (
  params: string
): Promise<IGetEditTagOrTagGroupResponse> => {
  const {data} = await apiClient.get(
    tagsAndFoldersEndpoint(`/tagsandfolders/tags/edit${params}`)
  );
  return data;
};

export const updateTagOrTagGroup = async (
  payload: IUpdateTagOrTagGroupRequest
): Promise<IUpdateTagOrTagGroupResponse> => {
  const {data} = await apiClient.put(
    tagsAndFoldersEndpoint('/tagsandfolders/tags'),
    payload
  );
  return data;
};
export const superMasterDocListGen = async (
  payload: ISuperMasterDocListGenReqPayload
): Promise<ISuperMasterDocListGenRes> => {
  const {data} = await apiClient.post(
    tagsAndFoldersEndpoint('/taggingconflict/super-master-doc-list'),
    payload
  );
  return data;
};

export const getBulkTagTallyReportStatus = async (
  payload: IGetBulkTagTallyReportStatusReqPayload
): Promise<IGetBulkTagTallyReportStatusRes> => {
  const {data} = await apiClient.get(
    tagsAndFoldersEndpoint('/taggingconflict/reports/tally/status'),
    {params: payload}
  );
  return data;
};

export const saveBulkTagReportData = async (
  payload: ISaveBulkTagReportDataReqPayload
): Promise<ISaveBulkTagReportDataRes> => {
  const {data} = await apiClient.post(
    tagsAndFoldersEndpoint('/taggingconflict/reports/tally'),
    payload
  );
  return data;
};

export const getTaggingConflictData = async (
  payload: IGetTaggingConflictDataReqPayload
): Promise<IGetTaggingConflictDataRes> => {
  const {data} = await apiClient.get(
    tagsAndFoldersEndpoint('/taggingconflict/data'),
    {params: payload}
  );
  return data;
};

export const getFoldersList = async (params: string): Promise<ITagsList[]> => {
  const {data} = await apiClient.get(
    tagsAndFoldersEndpoint(`/tagsandfolders/list/folder${params}`)
  );
  return data;
};

export const deleteTagGroupOrTag = async (
  payload: IDeleteTagGroupOrTagRequest
): Promise<string> => {
  const {data} = await apiClient.delete(
    tagsAndFoldersEndpoint('/tagsandfolders/tags'),
    {data: payload}
  );
  return data;
};

export const addFolderGroup = async (
  payload: IAddFolderGroupRequest
): Promise<IAddFolderGroupResponse> => {
  try {
    const {data} = await apiClient.post(
      tagsAndFoldersEndpoint('/tagsandfolders/folder-groups'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'TagsAndFolders'
    );
  }
};

export const getFolderOrFolderGroup = async (
  params: IGetFolderOrFolderGroupRequestParams
): Promise<IGetFolderOrFolderGroupResponse> => {
  try {
    const {data} = await apiClient.get(
      tagsAndFoldersEndpoint(
        `/tagsandfolders/folders/edit?tagId=${params.tagId}&securityGroupID=${params.securityGroupID}`
      )
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'TagsAndFolders'
    );
  }
};

export const updateFolderOrFolderGroup = async (
  payload: IUpdateFolderOrFolderGroupsRequest
): Promise<IUpdateFolderOrFolderGroupsResponse> => {
  try {
    const {data} = await apiClient.put(
      tagsAndFoldersEndpoint('/tagsandfolders/folders'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'TagsAndFolders'
    );
  }
};

export const deleteFolderGroupOrFolder = async (payload: {
  tagId: string;
  securityGroupID: string;
}) => {
  const {data} = await apiClient.delete(
    tagsAndFoldersEndpoint(
      `/tagsandfolders/folders?tagId=${payload.tagId}&securityGroupID=${payload.securityGroupID}`
    )
  );
  return data;
};

export const moveFolderOrFolderGroup = async (
  payload: IMoveFolderOrFolderGroup
): Promise<IMoveFolderOrFolderGroupRes> => {
  try {
    const {data} = await apiClient.post(
      tagsAndFoldersEndpoint('/tagsandfolders/folders/move'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'TagsAndFolders'
    );
  }
};

export const moveTagOrTagGroup = async (
  payload: IMoveTagOrTagGroupPayload
): Promise<IMoveTagOrTagGroupResponse> => {
  try {
    const {data} = await apiClient.post(
      tagsAndFoldersEndpoint('/tagsandfolders/tags/move'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'TagsAndFolders'
    );
  }
};
